import React, { useState, useRef, useEffect } from "react";

const Popover = ({ title, content, children }) => {
  const [show, setShow] = useState(false);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  // Hide the popover if a click is detected outside the popover/trigger.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        setShow(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div
      className="relative inline-block"
      ref={triggerRef}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <div onClick={() => setShow((prev) => !prev)}>{children}</div>
      {show && (
        <div
          ref={popoverRef}
          className="absolute left-full ml-2 top-1/2 transform -translate-y-1/2 z-10 w-64 text-sm text-gray-500 transition-opacity duration-300 bg-white border border-gray-200 rounded-lg shadow-xs dark:text-gray-400 dark:border-gray-600 dark:bg-gray-800"
        >
          <div className="px-3 py-2 bg-gray-100 border-b border-gray-200 rounded-t-lg dark:border-gray-600 dark:bg-gray-700">
            <h3 className="font-semibold text-gray-900 dark:text-white">{title}</h3>
          </div>
          <div className="px-3 py-2">
            <p>{content}</p>
          </div>
        </div>
      )}
    </div>
  );
};

function SchedulePreferences({
  preferences,
  setPreferences,
  ignoreRecitations,
  setIgnoreRecitations,
  ignoreDuplicateSections,
  setIgnoreDuplicateSections,
}) {
  // State to toggle expansion of the scoring settings section
  const [scoringExpanded, setScoringExpanded] = useState(false);

  // Reusable info icon component
  const InfoIcon = ({ title, content }) => (
    <Popover title={title} content={content}>
      <button type="button" className="ml-1 focus:outline-none inline-flex items-center">
        <svg
          className="w-4 h-4 text-gray-400 hover:text-gray-500"
          aria-hidden="true"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
            clipRule="evenodd"
          ></path>
        </svg>
      </button>
    </Popover>
  );

  // Helper component for a slider input with label and info icon
  const WeightSlider = ({ label, value, onChange, infoTitle, infoContent, disabled = false }) => (
    <div>
      <label className="text-sm font-medium text-gray-700 flex items-center">
        {label}: <span className="ml-1 font-bold">{value}</span>
        <InfoIcon title={infoTitle} content={infoContent} />
      </label>
      <input
        type="range"
        min="1"
        max="5"
        step="1"
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value, 10))}
        disabled={disabled}
        className="w-full mt-1"
      />
    </div>
  );

  return (
    <div>
      <h2 className="text-lg font-bold mb-2">Schedule Preferences</h2>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-sm font-medium text-gray-700">Desired Earliest Start:</label>
          <input
            type="time"
            value={preferences.desiredStart}
            onChange={(e) =>
              setPreferences({ ...preferences, desiredStart: e.target.value })
            }
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700">Desired Latest End:</label>
          <input
            type="time"
            value={preferences.desiredEnd}
            onChange={(e) =>
              setPreferences({ ...preferences, desiredEnd: e.target.value })
            }
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700">Lunch Start:</label>
          <input
            type="time"
            value={preferences.lunchStart}
            onChange={(e) =>
              setPreferences({ ...preferences, lunchStart: e.target.value })
            }
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700">Lunch End:</label>
          <input
            type="time"
            value={preferences.lunchEnd}
            onChange={(e) =>
              setPreferences({ ...preferences, lunchEnd: e.target.value })
            }
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
          />
        </div>
      </div>

      <div className="mt-4 flex flex-col space-y-2">
        {/* Ignore Recitations in Paired Courses */}
        <div className="flex items-center">
          <input
            id="ignore-recitation-pairs"
            type="checkbox"
            checked={ignoreRecitations}
            onChange={(e) => setIgnoreRecitations(e.target.checked)}
            className="h-4 w-4 text-blue-600 border-gray-300 rounded"
          />
          <label htmlFor="ignore-recitation-pairs" className="ml-2 text-sm font-medium text-gray-700">
            Ignore Recitations in Paired Courses
          </label>
          <InfoIcon
            title="Recitations Info"
            content="When enabled, for courses with both lecture and recitation (or tutorial) pairings, only the lecture times will be used."
          />
        </div>

        {/* Ignore Duplicate Section Differences */}
        <div className="flex items-center">
          <input
            id="ignore-duplicate-sections"
            type="checkbox"
            checked={ignoreDuplicateSections}
            onChange={(e) => setIgnoreDuplicateSections(e.target.checked)}
            className="h-4 w-4 text-blue-600 border-gray-300 rounded"
          />
          <label htmlFor="ignore-duplicate-sections" className="ml-2 text-sm font-medium text-gray-700">
            Ignore Duplicate Section Differences
          </label>
          <InfoIcon
            title="Duplicate Sections Info"
            content="When enabled, schedule combinations that differ only by section (but have identical meeting times) are deduplicated."
          />
        </div>

        {/* After Class Break */}
        <div className="flex items-center">
          <input
            id="after-class-break"
            type="checkbox"
            checked={preferences.afterClassBreakEnabled}
            onChange={(e) =>
              setPreferences({ ...preferences, afterClassBreakEnabled: e.target.checked })
            }
            className="h-4 w-4 text-blue-600 border-gray-300 rounded"
          />
          <label htmlFor="after-class-break" className="ml-2 text-sm font-medium text-gray-700">
            After Class Break
          </label>
          <InfoIcon
            title="After Class Break Info"
            content="When enabled, ensures a minimum break time after each class."
          />
        </div>
        {/* Input for After Class Break Duration */}
        {preferences.afterClassBreakEnabled && (
          <div className="ml-2">
            <label className="text-sm font-medium text-gray-700">
              After Class Break Duration (minutes):
            </label>
            <input
              type="number"
              value={preferences.afterClassBreakDuration}
              onChange={(e) =>
                setPreferences({
                  ...preferences,
                  afterClassBreakDuration: e.target.value,
                })
              }
              className="ml-1 w-20 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm mt-1"
              min="1"
            />
          </div>
        )}
      </div>

      {/* Collapsible Section: Schedule Scoring Settings */}
      <div className="mt-2 pt-2 border-t">
        <button
          onClick={() => setScoringExpanded(!scoringExpanded)}
          className="flex justify-between items-center w-full text-left text-lg font-bold text-gray-800"
        >
          <span>Schedule Scoring Settings</span>
          <svg
            className={`w-5 h-5 transition-transform ${scoringExpanded ? "rotate-180" : "rotate-0"}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {scoringExpanded && (
          <div className="mt-4 space-y-4">
            <div className="flex items-center">
              <input
                id="enforce-start-end"
                type="checkbox"
                checked={preferences.enforceStartEnd}
                onChange={(e) =>
                  setPreferences({ ...preferences, enforceStartEnd: e.target.checked })
                }
                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
              <label htmlFor="enforce-start-end" className="ml-2 text-sm font-medium text-gray-700">
                Enforce Start/End Times
              </label>
              <InfoIcon
                title="Enforce Start/End Times"
                content="When enabled, schedules that have any meeting starting before your desired start time or ending after your desired end time will be excluded."
              />
            </div>
            <div className="flex items-center">
              <input
                id="enforce-lunch"
                type="checkbox"
                checked={preferences.enforceLunch}
                onChange={(e) =>
                  setPreferences({ ...preferences, enforceLunch: e.target.checked })
                }
                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
              <label htmlFor="enforce-lunch" className="ml-2 text-sm font-medium text-gray-700">
                Enforce Lunch Break
              </label>
              <InfoIcon
                title="Enforce Lunch Break"
                content="When enabled, schedules that have any meeting overlapping your lunch period will be excluded."
              />
            </div>
            <WeightSlider
              label="Weight for Early Start"
              value={preferences.weightStart}
              onChange={(val) => setPreferences({ ...preferences, weightStart: val })}
              infoTitle="Weight for Early Start"
              infoContent="Determines how heavily a schedule is penalized if it starts earlier than desired."
            />
            <WeightSlider
              label="Weight for Late End"
              value={preferences.weightEnd}
              onChange={(val) => setPreferences({ ...preferences, weightEnd: val })}
              infoTitle="Weight for Late End"
              infoContent="Determines how heavily a schedule is penalized if it ends later than desired."
            />
            <WeightSlider
              label="Weight for Lunch Overlap"
              value={preferences.weightLunch}
              onChange={(val) => setPreferences({ ...preferences, weightLunch: val })}
              infoTitle="Weight for Lunch Overlap"
              infoContent="Determines how heavily a schedule is penalized if it overlaps your lunch break."
            />
            <WeightSlider
              label="Weight for After Class Break"
              value={preferences.weightAfterClassBreak}
              onChange={(val) => setPreferences({ ...preferences, weightAfterClassBreak: val })}
              infoTitle="Weight for After Class Break"
              infoContent="Determines how heavily a schedule is penalized if the break between classes is less than desired."
              disabled={!preferences.afterClassBreakEnabled}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default SchedulePreferences;
